/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   29/08/2022
 */

import React from "react";
import {Autocomplete, Badge, Box, TextField} from "@mui/material";
import {EventTypeOption, eventTypes, getEventType} from "../calendar/event-types";
import { useDispatch, useSelector } from "react-redux";
import {State} from "../state/store";
import {EventType} from "../interfaces/Global";
import { add } from "../state/reducers/recent-event-types";

export interface EventTypeSelectorProps {
	value: string
	onChange: (value: string) => void
}

const standardOptions = eventTypes.filter(t => t.parentId).map(t => getEventType(t.id)) as EventTypeOption[];

export default function EventTypeSelector(props: EventTypeSelectorProps) {

	const dispatch = useDispatch();
	const recentEventTypes = useSelector((s:State) => s.recentEventTypes);

	const options = React.useMemo(() => {
		const recentlyUsedOptions: EventTypeOption[] = recentEventTypes
			.map(t => getEventType(t) as EventTypeOption ).filter(t => !!t)
			.map(t => ({
				...t,
				name: t.name + " (" + t.parent?.name + ")",
				parent: {
					...(t.parent as EventType),
					name: "Recently Used Types"
				}
			}));
		return [
			...recentlyUsedOptions,
			...standardOptions
		];
	}, [recentEventTypes]);

	return (
		<Autocomplete
			value={options.find(t => t.id === props.value)}
			onChange={(event, option) => {
				dispatch(add(option?.id ?? "AL"));
				props.onChange(option?.id || "");
			}}
			options={options}
			autoHighlight
			getOptionLabel={(option) => String(option.name)}
			groupBy={(option) => option.parent?.name || "N/A"}
			renderOption={(props, option) =>
				<Box key={option.parentId + option.id} component={"li"} {...props}>
					<Badge
						variant={"dot"}
						sx={{ mr: .5, zIndex: 0 }}
						color={option.isUnavailable ? "error" : "success"}
						invisible={false}
						overlap={"circular"}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'right',
						}}
					>
						{option.icon}
					</Badge>
					{option.name}
				</Box>
			}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Type"
					variant={"standard"}
					inputProps={{
						...params.inputProps,
						autoComplete: 'new-password', // disable autocomplete and autofill
					}}
				/>
			)}
		/>
	);

}